import { MultipartFailureKind, MultipartReconciliationVerdict, MultipartSessionStatus } from '@atlas/types'
import type { FileDTO } from '@/services/file.service'
import type { MultipartPartsResponse } from '@/services/file-multipart.service'
import { fileMultipartService } from '@/services/file-multipart.service'
import { UploadRecoveryAction } from '../../upload-recovery'
import { MultipartUploadError, multipartErrorCode, multipartErrorDetails } from './multipart-errors'

/** The bounded backoff one finishing window uses. The last value is the ceiling, not the last poll. */
export const FINISHING_POLL_INTERVALS_SECONDS = [2, 5, 10, 30]

/**
 * The completion refusal that means cancellation owns this attempt: the
 * authoritative session is `ABORTING` or `ABORTED`, so completion may not
 * mutate S3 and may not continue.
 *
 * The code alone identifies the condition; the `status` it carries says which
 * stage of that same condition the server currently observes.
 */
export const MULTIPART_SESSION_NOT_ACTIVE = 'MULTIPART_SESSION_NOT_ACTIVE'

/** Injected so a test can drive the whole finishing window without waiting for real time to pass. */
export interface FinishingClock {
	sleep: (ms: number) => Promise<void>
	now: () => number
}

const realClock: FinishingClock = {
	sleep: (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
	now: () => Date.now(),
}

/**
 * Drives a session to its terminal outcome after the parts are in place.
 *
 * `POST /complete` is called first and is the only mutating step; everything
 * after it is bounded `GET` polling, because a completion whose response was
 * lost must be observed, not re-attempted. The poll budget is the one the
 * backend returns — the client never derives it from an API timeout.
 */
export async function finalizeSession(params: {
	fileId: string
	sessionId: string
	clock?: FinishingClock
}): Promise<FileDTO> {
	const clock = params.clock ?? realClock
	const completion = await attemptCompletion(params.fileId, params.sessionId, clock)
	if (completion) return completion
	return pollUntilResolved({ ...params, clock, observeFirst: false })
}

/**
 * Watches a session that is ALREADY finalizing, without completing it first.
 *
 * The entry point for every re-entry into a `COMPLETING`/`COMPLETED` session —
 * a resumed attempt, a state-aware conflict, or a `Retry status check`. All
 * three start with a read, and only a takeover verdict from that read calls
 * `POST /complete`, because a fresh lease held by another finalizer must be
 * waited out rather than raced.
 */
export function observeSession(params: {
	fileId: string
	sessionId: string
	clock?: FinishingClock
	/**
	 * A read the caller has ALREADY issued for this session. Consumed as the
	 * first observation instead of being re-issued, so a caller that reconciled
	 * the session to discover it was finalizing does not ask the same question
	 * twice in a row.
	 */
	observed?: MultipartPartsResponse
}): Promise<FileDTO> {
	return pollUntilResolved({ ...params, clock: params.clock ?? realClock, observeFirst: true })
}

/**
 * Watches a cancellation this client is NOT the owner of.
 *
 * A completion refused with `MULTIPART_SESSION_NOT_ACTIVE` lost the race to
 * cancellation, and the actor that started that cancellation may be another
 * tab, another device or another flow. So this client only reads: it issues no
 * `DELETE`, calls no completion again, and never takes over an abort it did
 * not start. `ABORTED` ends the attempt; `ABORTING` is observed
 * until it gets there, and only then is a fresh attempt offered.
 */
export async function observeCancellation(params: {
	fileId: string
	sessionId: string
	/** The authoritative status the server reported — the contract carries `ABORTING` or `ABORTED`. */
	status?: MultipartSessionStatus
	clock?: FinishingClock
	/** A read the caller already has, consumed as the first observation instead of being re-issued. */
	observed?: MultipartPartsResponse
}): Promise<FileDTO> {
	if (params.status === MultipartSessionStatus.ABORTED) throw cancelledAttemptError(params.sessionId)
	if (params.status !== MultipartSessionStatus.ABORTING) {
		// The contract carries only those two statuses here. Anything else is an
		// answer this client cannot act on: an unrecognized status is treated as
		// terminal, non-retryable, never a completion retry.
		throw new MultipartUploadError(failureMessage(null), MultipartFailureKind.RESTART_REQUIRED, {
			code: MULTIPART_SESSION_NOT_ACTIVE,
			...(params.status ? { status: params.status } : {}),
			multipartSessionId: params.sessionId,
			actions: [UploadRecoveryAction.START_OVER],
		})
	}
	return pollUntilResolved({
		fileId: params.fileId,
		sessionId: params.sessionId,
		clock: params.clock ?? realClock,
		observeFirst: true,
		...(params.observed ? { observed: params.observed } : {}),
		stillWaitingMessage: 'This upload is being cancelled. Please check again in a moment.',
	})
}

/**
 * The one terminal answer a cancelled attempt gets.
 *
 * It carries no `releaseSession`: this client never owned the cancellation, so
 * there is nothing here for it to release, and the only way forward is a fresh
 * attempt.
 */
export function cancelledAttemptError(sessionId?: string, resumeCleared = false): MultipartUploadError {
	return new MultipartUploadError(
		'This upload was cancelled. You can start it again.',
		MultipartFailureKind.RESTART_REQUIRED,
		{
			status: MultipartSessionStatus.ABORTED,
			...(sessionId ? { multipartSessionId: sessionId } : {}),
			...(resumeCleared ? { resumeCleared: true } : {}),
			actions: [UploadRecoveryAction.START_OVER],
		},
	)
}

/**
 * One completion call, resolving the File when it finished and answering
 * `null` when the session is still finalizing — the case that hands over to
 * polling rather than calling completion again.
 */
async function attemptCompletion(fileId: string, sessionId: string, clock: FinishingClock): Promise<FileDTO | null> {
	try {
		const result = await fileMultipartService.complete(fileId, sessionId)
		// The discriminant, not the presence of a field: a still-finalizing answer
		// carries no File and a confirmed one always does.
		return result.status === MultipartSessionStatus.CONFIRMED ? result.file : null
	} catch (error) {
		if (multipartErrorCode(error) === MULTIPART_SESSION_NOT_ACTIVE) {
			const status = multipartErrorDetails(error).status
			return observeCancellation({
				fileId,
				sessionId,
				clock,
				...(typeof status === 'string' ? { status: status as MultipartSessionStatus } : {}),
			})
		}
		throw toMultipartFailure(error)
	}
}

/** Bounded observation of a session that is still finalizing. */
async function pollUntilResolved(params: {
	fileId: string
	sessionId: string
	clock: FinishingClock
	/** Read once before the first interval — for a session that was already finalizing when this window opened. */
	observeFirst: boolean
	/** A read the caller already has; used for the first observation rather than issuing an identical one. */
	observed?: MultipartPartsResponse
	/** What a spent budget says the session is still doing. Finalizing unless the caller is observing a cancellation. */
	stillWaitingMessage?: string
}): Promise<FileDTO> {
	const startedAt = params.clock.now()
	let budgetSeconds = FINISHING_POLL_INTERVALS_SECONDS[FINISHING_POLL_INTERVALS_SECONDS.length - 1]
	let attempt = 0
	let observe = params.observeFirst
	let carried = params.observed

	for (;;) {
		if (observe) {
			observe = false
		} else {
			const interval = FINISHING_POLL_INTERVALS_SECONDS[Math.min(attempt, FINISHING_POLL_INTERVALS_SECONDS.length - 1)]
			await params.clock.sleep(interval * 1000)
			attempt += 1
		}

		const state = carried ?? (await fileMultipartService.getParts(params.fileId, params.sessionId))
		carried = undefined
		budgetSeconds = state.finishingPollBudgetSeconds ?? budgetSeconds
		const resolved = await applyFinishingState(params.fileId, params.sessionId, state, params.clock)
		if (resolved) return resolved

		if ((params.clock.now() - startedAt) / 1000 >= budgetSeconds) {
			// Automatic polling stops; the session and its local metadata are
			// deliberately preserved so a status re-check continues the same session.
			// Retry status check / Close only, from either window: cancelling is
			// illegal while a session is finalizing, and a session already being
			// cancelled belongs to whoever started that — never to this observer.
			// Close deliberately leaves the session and its local metadata in place.
			throw new MultipartUploadError(
				params.stillWaitingMessage ?? 'Upload is still finalizing.',
				MultipartFailureKind.RETRYABLE,
				{
					status: state.status,
					multipartSessionId: params.sessionId,
					actions: [UploadRecoveryAction.RETRY_STATUS_CHECK, UploadRecoveryAction.CLOSE],
				},
			)
		}
	}
}

/** What one observation of a finishing session means: resolved, terminal, or keep waiting. */
async function applyFinishingState(
	fileId: string,
	sessionId: string,
	state: MultipartPartsResponse,
	clock: FinishingClock,
): Promise<FileDTO | null> {
	if (state.status === MultipartSessionStatus.CONFIRMED && state.file) return state.file
	// Cancellation owns the session. Reading that is all this client does about
	// it: `ABORTED` ends the attempt, `ABORTING` keeps the read-only window open
	// until the actor that started the abort converges it.
	if (state.status === MultipartSessionStatus.ABORTED) throw cancelledAttemptError(sessionId)
	if (state.status === MultipartSessionStatus.ABORTING) return null
	if (state.status === MultipartSessionStatus.FAILED) {
		throw new MultipartUploadError(failureMessage(state.failureCode), MultipartFailureKind.RESTART_REQUIRED, {
			code: state.failureCode,
			status: state.status,
			multipartSessionId: sessionId,
			actions: [UploadRecoveryAction.START_OVER],
		})
	}
	if (state.status === MultipartSessionStatus.EXPIRED || state.verdict === MultipartReconciliationVerdict.EXPIRE) {
		throw new MultipartUploadError(
			'This upload could not be finalized and must be restarted.',
			MultipartFailureKind.RESTART_REQUIRED,
			{
				status: state.status,
				verdict: state.verdict,
				multipartSessionId: sessionId,
				actions: [UploadRecoveryAction.START_OVER],
			},
		)
	}
	// Only `POST /complete` performs takeover — a read never advances finalization.
	if (
		state.verdict === MultipartReconciliationVerdict.RETRY_COMPLETION ||
		state.verdict === MultipartReconciliationVerdict.RETRY_VALIDATION ||
		state.verdict === MultipartReconciliationVerdict.FINALIZE_FAILED
	) {
		return attemptCompletion(fileId, sessionId, clock)
	}
	return null
}

/** The failure-specific wording each terminal finalization code gets. Internal diagnostics never appear here. */
export function failureMessage(failureCode?: string | null): string {
	switch (failureCode) {
		case 'SIZE_MISMATCH':
			return 'The uploaded file does not match the expected size. Please upload it again.'
		case 'MULTIPART_COMPLETION_REJECTED':
			return 'Storage rejected this upload while assembling it. Please upload the file again.'
		case 'MULTIPART_COMPLETION_UNRECOVERABLE':
			return 'This upload could not be completed. Please upload the file again.'
		case 'CLASSIFICATION_REJECTED':
			return 'The classification for this upload could not be applied. Please check it and upload again.'
		case 'FINAL_OBJECT_CLEANUP_REQUIRED':
			return 'This upload failed and needs attention from support. You can start a new upload.'
		default:
			return 'This upload could not be completed safely. Please upload the file again.'
	}
}

/** Maps a coded completion response onto the outcome the surface acts on. */
export function toMultipartFailure(error: unknown): unknown {
	const code = multipartErrorCode(error)
	if (!code) return error
	if (code === 'FINAL_OBJECT_CLEANUP_FAILED') {
		return new MultipartUploadError(
			'Finishing this upload did not complete. Please try again.',
			MultipartFailureKind.RETRYABLE,
			{
				code,
				actions: [UploadRecoveryAction.RETRY_STATUS_CHECK, UploadRecoveryAction.CLOSE],
			},
		)
	}
	if (code === 'MULTIPART_PART_SET_INVALID') return error
	// Every remaining coded completion answer is terminal for this session:
	// polling stops, local resume state is already cleared, and the only way
	// forward is a fresh attempt.
	return new MultipartUploadError(failureMessage(code), MultipartFailureKind.RESTART_REQUIRED, {
		code,
		actions: [UploadRecoveryAction.START_OVER],
	})
}
